import moment from 'moment';
import api, { request } from '../../api';
import { IErrorsStore } from '../errors/store';
import { IActionGroup, IStore } from '../index';
import { IProfileStore, ISource, IStripeToken, IUserStore } from './store';

export interface IProfileActions extends IActionGroup {
  addCustomerSource: (token: IStripeToken) => { user?: IUserStore; errors?: IErrorsStore };
  deleteCustomerSource: (source: ISource) => { user?: IUserStore; errors?: IErrorsStore };
  getOrRetrieve: () => { user?: IUserStore; errors?: IErrorsStore };
  updatePicture: (base64: string) => { user?: IUserStore; errors?: IErrorsStore };
  updateProfile: (data: IProfileStore) => { user?: IUserStore; errors?: IErrorsStore };
  updateUser: (data: IUserStore) => { user?: IUserStore; errors?: IErrorsStore };
}

const actions: IActionGroup = {
  addCustomerSource: async (state: IStore, token: IStripeToken) => {
    const response = await request.post(`${api.payments}`, { token: token.id });
    if (response.error) {
      return { errors: { paymentMethods: { msg: 'Error adding your new payment method' } } };
    }

    const sources = state.user.customer ? state.user.customer.sources : [];
    return {
      user: {
        ...state.user,
        customer: {
          ...(state.user.customer || {}),
          sources: [...sources, response]
        }
      }
    };
  },
  deleteCustomerSource: async (state: IStore, source: ISource) => {
    const response = await request.delete(`${api.payments}${source.djstripeId}`);
    if (response.error) {
      return { errors: { paymentMethods: { msg: 'Error deleting your payment method' } } };
    }
    const sources = state.user.customer ? state.user.customer.sources : [];

    return {
      user: {
        ...state.user,
        customer: {
          ...(state.user.customer || {}),
          sources: sources.filter(src => src.djstripeId !== source.djstripeId)
        }
      }
    };
  },

  getOrRetrieve: async (state: IStore) => {
    if (state.user && state.user.id > 0) {
      return { user: state.user };
    }

    const newUser = await request.get(api.user);
    if (newUser.error) {
      return { errors: { profile: { msg: 'Error fetching your user picture' } } };
    }

    return {
      ...state,
      user: {
        ...state.user,
        ...newUser,
        customer: newUser.customer || { sources: [] },
        profile: {
          ...newUser.profile,
          hasGhin: !!newUser.profile.ghinNumber
        }
      }
    };
  },
  updatePicture: async (state: IStore, base64: string) => {
    const regex = /data:image\/([a-zA-Z]+);/;
    const found = base64.match(regex);
    const extension = found ? found[1] : 'png';

    const filename = String((Math.random() * 10000000000000).toFixed()) + `.${extension}`;
    const formData = new FormData();

    const res = await fetch(base64);
    const blob = await res.blob();

    formData.append('image', blob, filename);

    const response = await request.multipart(`${api.profile}${state.user.id}/`, formData);

    if (response.error) {
      return { errors: { profile: { msg: 'Error updating the user picture' } } };
    }

    const profile = response;
    return {
      user: {
        ...state.user,
        profile: {
          ...profile,
          hasGhin: !!profile.ghinNumber
        }
      }
    };
  },
  updateProfile: async (state: IStore, data: IProfileStore) => {
    const response = await request.patch(`${api.profile}${state.user.id}/`, {
      birth_date: moment(data.birthDate).format('YYYY-MM-DD'),
      gender: data.gender,
      ghin_number: data.ghinNumber
    });

    if (response.error) {
      return { errors: { profile: { msg: 'Error updating the user profile' } } };
    }

    return {
      user: {
        ...state.user,
        profile: {
          ...state.user.profile,
          ...response,
          hasGhin: !!response.ghinNumber
        }
      }
    };
  },
  updateUser: async (state: IStore, data: IUserStore) => {
    const response = await request.patch(`${api.user}`, {
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
      profile: {
        birth_date: moment(data.profile.birthDate).format('YYYY-MM-DD'),
        gender: data.profile.gender,
        ghin_number: data.profile.ghinNumber
      }
    });

    if (response.error) {
      return { errors: { profile: { msg: 'Error updating the user profile' } } };
    }

    const newUser = response;
    return {
      user: {
        ...state.user,
        ...newUser,
        profile: {
          ...state.user.profile,
          ...newUser.profile,
          hasGhin: !!newUser.profile.ghinNumber
        }
      }
    };
  }
};

export default actions;
